// src/screens/Login.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  deleteUser,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  updateDoc,
  collectionGroup,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
} from "firebase/firestore";
import { auth, db, functions } from "../../firebase";
import Logo from "../../assets/Image/Effitrack.png";
import { FiEye, FiEyeOff, FiCheck, FiX } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { createSession, cleanupOldSessions } from "../../utils/sessionService";
import { logAuditEvent, AUDIT_ACTIONS } from "../../utils/auditService";
import {
  validatePassword,
  getPasswordStrength,
  getStrengthLabel,
  getPasswordRequirements,
  getPasswordPolicy,
  DEFAULT_POLICY,
} from "../../utils/passwordValidation";
import {
  generateUniqueSlug,
  registerSlug,
  getDefaultPublicProfile
} from "../../utils/slugUtils";
import { useAuth } from "../../context/AuthContext";

const Login = () => {
  const { t } = useTranslation("auth");
  const { refreshSubscription } = useAuth() || {};

  // UI state
  const [isLogin, setIsLogin] = useState(true);
  const [isEmployeeMode, setIsEmployeeMode] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // UX state
  const [loading, setLoading] = useState(false);
  const [routing, setRouting] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [inviteStatus, setInviteStatus] = useState(""); // For showing invite validation
  const [pendingAdminInvite, setPendingAdminInvite] = useState(null); // For admin invite auto-detection
  const [checkingAdminInvite, setCheckingAdminInvite] = useState(false);
  const [checkingEmployeeInvite, setCheckingEmployeeInvite] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false); // For showing login redirect

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState(
    getPasswordRequirements(DEFAULT_POLICY)
  );

  // Password policy state (dynamically loaded for employee/admin invites)
  const [passwordPolicy, setPasswordPolicy] = useState(DEFAULT_POLICY);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for session expiration redirect
  useEffect(() => {
    const reason = searchParams.get("reason");
    const mode = searchParams.get("mode");
    const reset = searchParams.get("reset");

    if (mode === "employee") {
      setIsEmployeeMode(true);
    } else if (mode === "admin") {
      setIsEmployeeMode(false);
    }

    if (reset === "complete") {
      setSuccess(t("loginPage.passwordResetComplete"));
    }

    if (reason === "session_expired") {
      setError(t("loginPage.sessionExpiredSecurity"));
    }

    if (reason === "session_expired" || mode || reset) {
      // Clear one-time URL parameters after applying them.
      window.history.replaceState({}, "", "/login");
    }
  }, [searchParams, t]);

  // Update password requirements when policy changes
  useEffect(() => {
    setPasswordRequirements(getPasswordRequirements(passwordPolicy));
  }, [passwordPolicy]);

  // Debounce timer ref for invite checking
  const debounceTimerRef = useRef(null);

  // Auto-check for invites when email changes (debounced)
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only check during signup mode
    if (isLogin) {
      setInviteStatus("");
      setPendingAdminInvite(null);
      return;
    }

    const trimmedEmail = email.trim().toLowerCase();

    // Only check if email looks valid (has @ and .)
    if (!trimmedEmail || !trimmedEmail.includes("@") || !trimmedEmail.includes(".")) {
      setInviteStatus("");
      setPendingAdminInvite(null);
      setIsAlreadyRegistered(false);
      return;
    }

    // Debounce the check by 500ms
    debounceTimerRef.current = setTimeout(async () => {
      const emailKeyFormatted = trimmedEmail.replace(/[@.]/g, "_");

      // Set checking state immediately
      if (isEmployeeMode) {
        setCheckingEmployeeInvite(true);
      } else {
        setCheckingAdminInvite(true);
      }

      try {
        // First, check if this email is already registered in the root users collection
        let isRegistered = false;
        try {
          const usersQuery = query(
            collection(db, "users"),
            where("emailLower", "==", trimmedEmail)
          );
          const usersSnap = await getDocs(usersQuery);
          isRegistered = !usersSnap.empty;
        } catch (usersQueryErr) {
          // If permission denied, skip the already-registered check
          console.log("Could not check users collection (may require auth):", usersQueryErr.code);
        }

        if (isRegistered) {
          setError(t("loginPage.emailAlreadyRegistered"));
          setIsAlreadyRegistered(true);
          setInviteStatus("");
          setPendingAdminInvite(null);
          setBusinessName("");
          setCheckingEmployeeInvite(false);
          setCheckingAdminInvite(false);
          return;
        }

        setIsAlreadyRegistered(false);

        if (isEmployeeMode) {
          // Check for employee invite
          const pEmpRef = doc(db, `pendingEmployees/${emailKeyFormatted}`);
          const pEmpSnap = await getDoc(pEmpRef);

          if (pEmpSnap.exists()) {
            const inviteData = pEmpSnap.data();
            setInviteStatus(t("loginPage.invitationFound", { name: inviteData.name || 'your admin' }));
            setError("");

            // Fetch business password policy
            if (inviteData.businessId) {
              try {
                const policy = await getPasswordPolicy(inviteData.businessId);
                setPasswordPolicy(policy);
                setPasswordRequirements(getPasswordRequirements(policy));
              } catch (policyErr) {
                console.error("Error fetching password policy:", policyErr);
              }
            }
          } else {
            setInviteStatus(t("loginPage.noInvitationFound"));
            setPasswordPolicy(DEFAULT_POLICY);
            setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
          }
          setCheckingEmployeeInvite(false);
        } else {
          // Check for admin invite
          const pAdmRef = doc(db, `pendingAdmins/${emailKeyFormatted}`);
          const pAdmSnap = await getDoc(pAdmRef);

          if (pAdmSnap.exists()) {
            const inviteData = pAdmSnap.data();
            setError("");

            if (inviteData.businessId) {
              try {
                const businessSnap = await getDoc(doc(db, "businesses", inviteData.businessId));
                if (businessSnap.exists()) {
                  const businessData = businessSnap.data();
                  setPendingAdminInvite({
                    ...inviteData,
                    businessName: businessData.businessName || "Unknown Business",
                  });
                  setBusinessName(businessData.businessName || "");

                  // Fetch password policy
                  try {
                    const policy = await getPasswordPolicy(inviteData.businessId);
                    setPasswordPolicy(policy);
                    setPasswordRequirements(getPasswordRequirements(policy));
                  } catch (policyErr) {
                    console.error("Error fetching password policy:", policyErr);
                  }
                } else {
                  setPendingAdminInvite(inviteData);
                }
              } catch (bizErr) {
                console.error("Error fetching business info:", bizErr);
                setPendingAdminInvite(inviteData);
              }
            } else {
              setPendingAdminInvite(inviteData);
            }
          } else {
            setPendingAdminInvite(null);
            setPasswordPolicy(DEFAULT_POLICY);
            setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
          }
          setCheckingAdminInvite(false);
        }
      } catch (err) {
        console.error("Error checking invite:", err);
        setInviteStatus("");
        setPendingAdminInvite(null);
        setIsAlreadyRegistered(false);
        setCheckingAdminInvite(false);
        setCheckingEmployeeInvite(false);
      }
    }, 500);

    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [email, isLogin, isEmployeeMode, t]);

  // ─────────────────────────────────────────────────────────────
  // On auth state change → route if user doc exists
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRouting(false);
        return;
      }
      try {
        const uref = doc(db, "users", user.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const ud = usnap.data();
          if (ud.isAdmin) {
            navigate("/admin/dashboard");
          } else if (ud.isEmployee) {
            navigate("/employee/dashboard");
          } else {
            // Fallback if role missing
            navigate("/dashboard");
          }
          return;
        }
      } catch (e) {
        console.warn("Auth routing error:", e);
      } finally {
        setRouting(false);
      }
    });
    return () => unsub();
  }, [navigate]);

  const highlights = useMemo(
    () => [
      t("loginPage.highlight1"),
      t("loginPage.highlight2"),
      t("loginPage.highlight3"),
    ],
    [t]
  );

  // ─────────────────────────────────────────────────────────────
  // Small helpers
  // ─────────────────────────────────────────────────────────────
  const emailKey = (e) => e.toLowerCase().trim().replace(/[@.]/g, "_");
  const lc = (s) => (s || "").toLowerCase().trim();

  const resetMessages = () => {
    setError("");
    setSuccess("");
    setIsAlreadyRegistered(false);
  };

  const toggleAuthMode = () => {
    resetMessages();
    setIsLogin((v) => !v);
    // Reset admin invite when switching between login/signup
    setPendingAdminInvite(null);
    setBusinessName("");
  };

  const toggleUserMode = () => {
    resetMessages();
    setInviteStatus("");
    setIsEmployeeMode((v) => !v);
    // Reset password policy to default when switching modes
    setPasswordPolicy(DEFAULT_POLICY);
    setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
    // Reset admin invite when switching between admin/employee mode
    setPendingAdminInvite(null);
    setBusinessName("");
  };

  // Check if employee invite exists when email changes (only in employee mode)
  const checkEmployeeInvite = async (emailInput) => {
    if (!isEmployeeMode || !emailInput || isLogin) {
      setInviteStatus("");
      setIsAlreadyRegistered(false);
      // Reset to default policy when not in employee signup mode
      setPasswordPolicy(DEFAULT_POLICY);
      setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
      return;
    }

    const trimmedEmail = emailInput.trim().toLowerCase();
    const emailKeyFormatted = trimmedEmail.replace(/[@.]/g, "_");

    try {
      // First, check if this email is already registered in the root users collection
      const usersQuery = query(
        collection(db, "users"),
        where("emailLower", "==", trimmedEmail)
      );
      const usersSnap = await getDocs(usersQuery);

      if (!usersSnap.empty) {
        // Email is already registered - show message and enable login redirect
        setError(t("loginPage.emailAlreadyRegistered"));
        setIsAlreadyRegistered(true);
        setInviteStatus("");
        setPasswordPolicy(DEFAULT_POLICY);
        setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
        return;
      }

      // Email not registered, check for pending invitation
      setIsAlreadyRegistered(false);
      const pEmpRef = doc(db, `pendingEmployees/${emailKeyFormatted}`);
      const pEmpSnap = await getDoc(pEmpRef);

      if (pEmpSnap.exists()) {
        const inviteData = pEmpSnap.data();
        setInviteStatus(t("loginPage.invitationFound", { name: inviteData.name || 'your admin' }));
        setError("");

        // Fetch business password policy
        if (inviteData.businessId) {
          try {
            const policy = await getPasswordPolicy(inviteData.businessId);
            setPasswordPolicy(policy);
            setPasswordRequirements(getPasswordRequirements(policy));
          } catch (policyErr) {
            console.error("Error fetching password policy:", policyErr);
            // Fallback to default policy on error
            setPasswordPolicy(DEFAULT_POLICY);
            setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
          }
        }
      } else {
        setInviteStatus(t("loginPage.noInvitationFound"));
        // Reset to default policy if no invite found
        setPasswordPolicy(DEFAULT_POLICY);
        setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
      }
    } catch (err) {
      console.error("Error checking invite:", err);
      setInviteStatus("");
      setIsAlreadyRegistered(false);
      // Reset to default policy on error
      setPasswordPolicy(DEFAULT_POLICY);
      setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
    }
  };

  // Check if admin invite exists when email changes (only in admin signup mode)
  const checkAdminInvite = async (emailInput) => {
    // Only check in admin signup mode (not employee mode, not login)
    if (isEmployeeMode || isLogin || !emailInput) {
      setPendingAdminInvite(null);
      setIsAlreadyRegistered(false);
      return;
    }

    const trimmedEmail = emailInput.trim().toLowerCase();
    if (!trimmedEmail || !trimmedEmail.includes("@")) {
      setPendingAdminInvite(null);
      setIsAlreadyRegistered(false);
      return;
    }

    const emailKeyFormatted = trimmedEmail.replace(/[@.]/g, "_");
    setCheckingAdminInvite(true);

    try {
      // First, check if this email is already registered in the root users collection
      const usersQuery = query(
        collection(db, "users"),
        where("emailLower", "==", trimmedEmail)
      );
      const usersSnap = await getDocs(usersQuery);

      if (!usersSnap.empty) {
        // Email is already registered - show message and enable login redirect
        setError(t("loginPage.emailAlreadyRegistered"));
        setIsAlreadyRegistered(true);
        setPendingAdminInvite(null);
        setBusinessName("");
        setPasswordPolicy(DEFAULT_POLICY);
        setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
        setCheckingAdminInvite(false);
        return;
      }

      // Email not registered, check for pending admin invitation
      setIsAlreadyRegistered(false);
      const pAdmRef = doc(db, `pendingAdmins/${emailKeyFormatted}`);
      const pAdmSnap = await getDoc(pAdmRef);

      if (pAdmSnap.exists()) {
        const inviteData = pAdmSnap.data();
        setError("");

        // Fetch business name from the business document
        if (inviteData.businessId) {
          try {
            const businessSnap = await getDoc(doc(db, "businesses", inviteData.businessId));
            if (businessSnap.exists()) {
              const businessData = businessSnap.data();
              setPendingAdminInvite({
                ...inviteData,
                businessName: businessData.businessName || "Unknown Business",
              });
              // Auto-populate the business name field (will be shown as read-only)
              setBusinessName(businessData.businessName || "");

              // Also fetch and apply the business password policy
              try {
                const policy = await getPasswordPolicy(inviteData.businessId);
                setPasswordPolicy(policy);
                setPasswordRequirements(getPasswordRequirements(policy));
              } catch (policyErr) {
                console.error("Error fetching password policy for admin invite:", policyErr);
              }
            } else {
              setPendingAdminInvite(inviteData);
            }
          } catch (bizErr) {
            console.error("Error fetching business info:", bizErr);
            setPendingAdminInvite(inviteData);
          }
        } else {
          setPendingAdminInvite(inviteData);
        }
      } else {
        setPendingAdminInvite(null);
        // Reset password policy to default when no invite
        setPasswordPolicy(DEFAULT_POLICY);
        setPasswordRequirements(getPasswordRequirements(DEFAULT_POLICY));
      }
    } catch (err) {
      console.error("Error checking admin invite:", err);
      setPendingAdminInvite(null);
      setIsAlreadyRegistered(false);
    } finally {
      setCheckingAdminInvite(false);
    }
  };

  /** Find employee doc by uid via collectionGroup */
  const findEmployeeByUid = async (uid) => {
    const qByUid = query(collectionGroup(db, "employees"), where("uid", "==", uid));
    const snap = await getDocs(qByUid);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { ref: d.ref, data: d.data(), businessId: d.ref.parent.parent.id };
  };

  /** Find claimable employee doc by emailLower (uid missing) */
  const findClaimableEmployeeByEmail = async (emailLower) => {
    const qByEmail = query(collectionGroup(db, "employees"), where("emailLower", "==", emailLower));
    const snap = await getDocs(qByEmail);
    if (snap.empty) return null;

    // Prefer the first with blank uid
    const claimable = snap.docs.find((d) => !d.data().uid);
    if (!claimable) return null;

    return {
      ref: claimable.ref,
      data: claimable.data(),
      businessId: claimable.ref.parent.parent.id,
    };
  };

  // ─────────────────────────────────────────────────────────────
  // Auth handler
  // ─────────────────────────────────────────────────────────────
  const handleAuth = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const trimmedEmail = email.trim();
      const lowerEmail = lc(trimmedEmail);

      if (isLogin) {
        // ── LOGIN FLOW ───────────────────────────────────────────
        const cred = await signInWithEmailAndPassword(auth, trimmedEmail, password);
        const uid = cred.user.uid;

        // Check users/{uid}
        const uref = doc(db, "users", uid);
        const usnap = await getDoc(uref);

        if (usnap.exists()) {
          const ud = usnap.data();
          const businessId = ud.businessId;

          // Create session and log audit event
          if (businessId) {
            await createSession(businessId, uid, cred.user.email);
            await logAuditEvent(businessId, AUDIT_ACTIONS.LOGIN, uid, cred.user.email, {
              method: "email_password",
            });
            // Cleanup old sessions in background
            cleanupOldSessions(businessId).catch(console.warn);
          }

          if (ud.isAdmin) {
            navigate("/admin/dashboard");
            return;
          }
          if (ud.isEmployee) {
            navigate("/employee/dashboard");
            return;
          }
          // No role? fallback
          navigate("/dashboard");
          return;
        }

        // Not in users yet → try employee flows (no business listing):
        // 1) Employee doc already has uid
        let found = await findEmployeeByUid(uid);

        // 2) If not found, claim a placeholder by emailLower
        if (!found) {
          found = await findClaimableEmployeeByEmail(lowerEmail);
          if (found) {
            await updateDoc(found.ref, {
              uid,
              status: "active",
              lastActive: serverTimestamp(),
            });
          }
        }

        if (!found) {
          setError(t("loginPage.userNotFound"));
          return;
        }

        // Create users/{uid} for the employee
        const emp = found.data;
        const userDoc = {
          uid,
          email: trimmedEmail,
          emailLower: lowerEmail,
          name: emp.name || "",
          businessId: found.businessId,
          isEmployee: true,
          isAdmin: false,
          role: emp.role || "Employee",
          status: "online",
          createdAt: serverTimestamp(),
        };
        await setDoc(uref, userDoc);

        // Create session and log audit event for employee
        await createSession(found.businessId, uid, trimmedEmail);
        await logAuditEvent(found.businessId, AUDIT_ACTIONS.LOGIN, uid, trimmedEmail, {
          method: "email_password",
          isFirstLogin: true,
        });

        navigate("/employee/dashboard");
        return;
      }

      // ── SIGNUP FLOW ────────────────────────────────────────────
      // Validate password before signup using business-specific policy
      const passwordValidation = validatePassword(password, passwordPolicy);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.errors.join("\n"));
        return;
      }

      // 1) If employee mode – redeem pending employee invite
      if (isEmployeeMode) {
        if (!name) {
          setError(t("loginPage.nameRequiredEmployee"));
          return;
        }

        // Create Auth user first so rule-protected invite reads have auth context
        const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
        const uid = cred.user.uid;
        const cleanupAuthUser = async () => {
          try {
            await deleteUser(cred.user);
          } catch (cleanupErr) {
            console.warn("Failed to clean up pending employee auth user:", cleanupErr);
          }
        };

        const pEmpRef = doc(db, `pendingEmployees/${emailKey(trimmedEmail)}`);

        try {
          const pEmpSnap = await getDoc(pEmpRef);

          if (!pEmpSnap.exists()) {
            await cleanupAuthUser();
            setError(t("loginPage.noEmployeeInvitation"));
            return;
          }

          const { businessId, role, employeeDocId } = pEmpSnap.data() || {};

          if (!businessId || !employeeDocId) {
            await cleanupAuthUser();
            setError(t("loginPage.employeeInviteMissingDetails"));
            return;
          }

          // Create users/{uid} FIRST (required for Firestore rules to recognize user)
          const userDoc = {
            uid,
            email: trimmedEmail,
            emailLower: lowerEmail,
            name,
            businessId,
            isEmployee: true,
            isAdmin: false,
            role: role || "Employee",
            status: "online",
            createdAt: serverTimestamp(),
          };
          try {
            await setDoc(doc(db, "users", uid), userDoc);
          } catch (err) {
            console.error('❌ Failed to create user document:', err);
            throw err;
          }

          // Update employee doc directly using stored document ID
          const empRef = doc(db, `businesses/${businessId}/employees`, employeeDocId);

          try {
            await updateDoc(empRef, {
              uid,
              name,
              email: trimmedEmail,
              emailLower: lowerEmail,
              status: "active",
              lastActive: serverTimestamp(),
            });
          } catch (err) {
            console.error('❌ Failed to update employee document:', err);
            console.error('Auth email:', cred.user.email);
            console.error('Trying to update with:', { uid, name, email: trimmedEmail, emailLower: lowerEmail });
            throw err;
          }

          // Delete invite
          try {
            await deleteDoc(pEmpRef);
          } catch (err) {
            console.error('❌ Failed to delete invite:', err);
            throw err;
          }

          // Create session and log audit event
          await createSession(businessId, uid, trimmedEmail);
          await logAuditEvent(businessId, AUDIT_ACTIONS.USER_CREATED, uid, trimmedEmail, {
            type: "employee_signup",
            role: role || "Employee",
          });
        } catch (inviteErr) {
          await cleanupAuthUser();
          throw inviteErr;
        }

        navigate("/employee/dashboard");
        return;
      }

      // 2) If NOT employee mode – handle admin invite OR new business
      if (!name) {
        setError(t("loginPage.nameRequired"));
        return;
      }

      const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      const uid = cred.user.uid;
      const cleanupAuthUser = async () => {
        try {
          await deleteUser(cred.user);
        } catch (cleanupErr) {
          console.warn("Failed to clean up pending admin auth user:", cleanupErr);
        }
      };

      const pAdmRef = doc(db, `pendingAdmins/${emailKey(trimmedEmail)}`);
      let pAdmSnap;
      try {
        pAdmSnap = await getDoc(pAdmRef);
      } catch (inviteErr) {
        await cleanupAuthUser();
        throw inviteErr;
      }

      if (pAdmSnap.exists()) {
        // Invited admin - fetch business password policy and validate
        const { businessId } = pAdmSnap.data() || {};

        if (!businessId) {
          await cleanupAuthUser();
          setError(t("loginPage.adminInviteMissingBusiness"));
          return;
        }

        // Fetch business document to get subscription info and password policy
        let businessData = null;
        try {
          const businessSnap = await getDoc(doc(db, "businesses", businessId));
          if (businessSnap.exists()) {
            businessData = businessSnap.data();
          }
        } catch (bizErr) {
          console.error("Error fetching business data:", bizErr);
        }

        // Validate against business password policy
        try {
          const policy = await getPasswordPolicy(businessId);
          const policyValidation = validatePassword(password, policy);
          if (!policyValidation.isValid) {
            await cleanupAuthUser();
            setError(policyValidation.errors.join("\n"));
            return;
          }
        } catch (policyErr) {
          console.error("Error fetching password policy for admin invite:", policyErr);
          // Continue with default policy validation (already done above)
        }

        // Build admin document with subscription info from business
        const adminDoc = {
          uid,
          email: trimmedEmail,
          emailLower: lowerEmail,
          name,
          businessId,
          isAdmin: true,
          isEmployee: false,
          status: "online",
          createdAt: serverTimestamp(),
          // Inherit subscription from business
          subscriptionPlan: businessData?.subscription?.plan || businessData?.subscriptionPlan || "starter",
          subscriptionStatus: businessData?.subscription?.status || businessData?.subscriptionStatus || "active",
          ...(businessData?.trialEndsAt && { trialEndsAt: businessData.trialEndsAt }),
        };

        await Promise.all([
          setDoc(doc(db, "users", uid), adminDoc),
          setDoc(doc(db, `businesses/${businessId}/users`, uid), adminDoc),
          // Add new admin's UID to business admins array for multi-admin support
          // Also store admin details (name, id) so employees can see the list
          updateDoc(doc(db, "businesses", businessId), {
            admins: arrayUnion(uid),
            adminDetails: arrayUnion({
              id: uid,
              name: name.trim(),
              email: trimmedEmail,
              isOwner: false,
              addedAt: new Date().toISOString(),
            }),
          }),
        ]);
        // Update invite to "accepted" status (instead of deleting) to show in TeamManagement UI
        await updateDoc(pAdmRef, {
          status: 'accepted',
          acceptedAt: serverTimestamp(),
          acceptedByUid: uid,
          acceptedByName: name.trim(),
        });

        // Create session and log audit event
        await createSession(businessId, uid, trimmedEmail);
        await logAuditEvent(businessId, AUDIT_ACTIONS.USER_CREATED, uid, trimmedEmail, {
          type: "admin_invite_accepted",
        });

        navigate("/admin/dashboard");
        return;
      }

      // New business creation (owner)
      if (!businessName) {
        await cleanupAuthUser();
        setError(t("loginPage.businessNameRequired"));
        return;
      }

      const newBizId = uuidv4();

      // Calculate trial end date (15 days from now - Scale plan trial)
      const trialEndDate = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000);

      // Generate unique slug for public website
      const businessSlug = await generateUniqueSlug(businessName);

      const businessDoc = {
        businessName,
        email: trimmedEmail,
        adminId: uid,
        addedBy: uid,
        ownerName: name.trim(),
        // Admin details array for employees to see available admins (hybrid messaging)
        admins: [uid],
        adminDetails: [{
          id: uid,
          name: name.trim(),
          email: trimmedEmail,
          isOwner: true,
          addedAt: new Date().toISOString(),
        }],
        createdAt: serverTimestamp(),
        subscriptionStatus: "trialing",

        // Subscription configuration (15-day Scale plan trial)
        subscription: {
          plan: "scale",
          status: "trialing",
          features: [
            "basic_invoices",
            "basic_estimates",
            "basic_customers",
            "unlimited_invoices",
            "unlimited_estimates",
            "scheduling",
            "reminders",
            "expense_tracking",
            "customer_portal",
            "basic_reports",
            "basic_automations",
            "email_support",
            "basic_team_invites",
            "public_website",
            "up_to_15_seats",
            "recurring_jobs",
            "automations",
            "custom_branding",
            "team_management",
            "sms_notifications",
            "advanced_scheduling",
            "time_tracking",
            "role_based_access",
            "live_chat_support",
            "location_tracking",
            "data_import",
            "advanced_analytics",
            "unlimited_seats",
            "approval_workflows",
            "custom_dashboards",
            "priority_support",
            "sso",
            "audit_logs",
            "custom_roles",
            "api_access",
            "white_label",
            "auto_routing",
            "custom_automations",
            "custom_domain"
          ],
          seats: -1,
          maxSeats: -1,
          billingCycle: "monthly",
          createdAt: serverTimestamp(),
        },

        trialEndsAt: trialEndDate,

        // Public website profile
        publicProfile: getDefaultPublicProfile(businessSlug),
      };

      const adminDoc = {
        uid,
        name,
        email: trimmedEmail,
        emailLower: lowerEmail,
        businessId: newBizId,
        isAdmin: true,
        isEmployee: false,
        status: "online",
        createdAt: serverTimestamp(),

        // User-level subscription (15-day Scale plan trial)
        subscriptionPlan: "scale",
        subscriptionStatus: "trialing",
        trialEndsAt: trialEndDate,
      };

      // IMPORTANT: Create in correct order!
      try {
        // 1. Create user document FIRST (so permission checks work)
        await setDoc(doc(db, "users", uid), adminDoc);

        // 2. Create business document SECOND
        await setDoc(doc(db, "businesses", newBizId), businessDoc);

        // 3. Create business user document LAST
        await setDoc(doc(db, `businesses/${newBizId}/users`, uid), adminDoc);

        // 4. Register the slug for public website
        if (businessSlug) {
          await registerSlug(businessSlug, newBizId);
        }

        // 5. Create session and log audit event for new business owner
        await createSession(newBizId, uid, trimmedEmail);
        await logAuditEvent(newBizId, AUDIT_ACTIONS.USER_CREATED, uid, trimmedEmail, {
          type: "business_owner_signup",
          businessName,
        });

      } catch (docErr) {
        console.error("Document creation failed:", docErr);
        await cleanupAuthUser();
        throw docErr;
      }

      // Refresh subscription in AuthContext so sidebar shows Scale plan
      if (refreshSubscription) {
        await refreshSubscription(newBizId);
      }

      // Redirect to onboarding for industry selection & price book seeding
      navigate(`/onboarding?businessId=${newBizId}`);
    } catch (err) {
      console.error("Error in Auth:", err);
      switch (err.code) {
        case "auth/invalid-credential":
          if (isEmployeeMode) {
            try {
              const pEmpSnap = await getDoc(doc(db, `pendingEmployees/${emailKey(email)}`));
              if (pEmpSnap.exists()) {
                setError(t("loginPage.employeeInvitePendingLogin"));
                break;
              }
            } catch (inviteCheckErr) {
              console.warn("Unable to check pending employee invite after login failure:", inviteCheckErr);
            }
            setError(t("loginPage.invalidEmployeeCredentials"));
            break;
          }
          setError(t("loginPage.invalidCredentials"));
          break;
        case "auth/email-already-in-use":
          setError(t("loginPage.emailAlreadyInUse"));
          break;
        case "auth/invalid-email":
          setError(t("loginPage.invalidEmailError"));
          break;
        case "auth/weak-password":
          setError(t("loginPage.weakPassword"));
          break;
        case "auth/user-not-found":
          setError(t("loginPage.noAccountFound"));
          break;
        case "auth/wrong-password":
          setError(t("loginPage.incorrectPassword"));
          break;
        case "auth/too-many-requests":
          setError(t("loginPage.tooManyAttempts"));
          break;
        case "auth/firebase-authentication-throttled":
          setError(t("loginPage.tooManyAttemptsShort"));
          break;
        case "auth/network-request-failed":
          setError(t("loginPage.networkError"));
          break;
        case "auth/user-disabled":
          setError(t("loginPage.accountDisabled"));
          break;
        case "permission-denied":
          setError(
            isEmployeeMode
              ? t("loginPage.permissionDeniedEmployee")
              : t("loginPage.permissionDeniedGeneral")
          );
          break;
        default:
          setError(err.message || t("loginPage.somethingWentWrong"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    resetMessages();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError(t("loginPage.enterEmailFirst"));
      return;
    }

    // Basic email validation
    if (!trimmedEmail.includes("@") || !trimmedEmail.includes(".")) {
      setError(t("loginPage.invalidEmailFormat"));
      return;
    }

    setLoading(true);
    try {
      // Employees with a pending (unaccepted) invite have no Firebase Auth account yet,
      // so a reset email cannot be sent. Detect this up front and point them at signup.
      if (isEmployeeMode) {
        try {
          const pEmpSnap = await getDoc(doc(db, `pendingEmployees/${emailKey(trimmedEmail)}`));
          if (pEmpSnap.exists()) {
            setError(t("loginPage.employeeInvitePendingReset"));
            return;
          }
        } catch (inviteCheckErr) {
          console.warn("Unable to check pending employee invite before password reset:", inviteCheckErr);
        }
      }

      const resetMode = isEmployeeMode ? "employee" : "admin";
      const continueUrl = `${window.location.origin}/login?mode=${resetMode}&reset=complete`;

      const sendReset = httpsCallable(functions, "sendPasswordResetEmail");
      await sendReset({
        email: trimmedEmail,
        continueUrl,
        mode: resetMode,
      });

      setSuccess(
        isEmployeeMode
          ? t("loginPage.passwordResetSentEmployee")
          : t("loginPage.passwordResetSentAdmin")
      );
    } catch (e) {
      console.error("Password reset error:", e);
      // Callable function errors are namespaced as "functions/<code>"; the
      // server returns generic success for unknown emails so user-not-found
      // never reaches this branch.
      switch (e.code) {
        case "functions/invalid-argument":
          setError(t("loginPage.invalidEmailFormat"));
          break;
        case "functions/resource-exhausted":
        case "auth/too-many-requests":
          setError(t("loginPage.tooManyResetAttempts"));
          break;
        default:
          if (isEmployeeMode) {
            setSuccess(t("loginPage.passwordResetSentGeneric"));
          } else {
            setError(t("loginPage.resetEmailFailed"));
          }
      }
    } finally {
      setLoading(false);
    }
  };

  if (routing) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-slate-500">{t("loginPage.checkingAuth")}</p>
      </div>
    );
  }
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-900">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(700px_at_0%_20%,rgba(16,185,129,0.14),transparent_50%),radial-gradient(900px_at_100%_0%,rgba(59,130,246,0.12),transparent_50%),radial-gradient(600px_at_80%_80%,rgba(14,165,233,0.1),transparent_45%)]"
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-14 space-y-8">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full overflow-hidden flex items-center justify-center">
              <img src={Logo} alt="EffiTrack" className="h-full w-full object-cover scale-110" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{t("loginPage.effitrackAccess")}</p>
              <p className="text-sm text-slate-600">{t("loginPage.tagline")}</p>
            </div>
          </div>
          <a
            href="mailto:support@effitrack.com"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-800 shadow-sm hover:border-emerald-300 hover:-translate-y-0.5 transition"
          >
            {t("loginPage.needHelp")}
          </a>
        </div>

        <div className="grid w-full max-w-6xl gap-8 lg:grid-cols-[1.05fr,0.95fr]">
          {/* Form card */}
          <section className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 text-slate-900 shadow-xl shadow-emerald-100/60">
            <div className="flex flex-col gap-6">
              <div className="space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-emerald-600">
                      {isEmployeeMode ? t("loginPage.employeeAccess") : t("loginPage.workspaceAccess")}
                    </p>
                    <h1 className="text-3xl font-semibold sm:text-4xl">
                      {isLogin ? t("loginPage.welcomeBack") : isEmployeeMode ? t("loginPage.joinYourTeam") : t("loginPage.createWorkspace")}
                    </h1>
                    <p className="text-sm text-slate-600">
                      {isLogin
                        ? isEmployeeMode
                          ? t("loginPage.signInEmployee")
                          : t("loginPage.signInAdmin")
                        : isEmployeeMode
                        ? t("loginPage.redeemInvitation")
                        : t("loginPage.createNewWorkspace")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleAuthMode}
                    className="self-start rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-slate-700 transition hover:border-emerald-300 hover:bg-white shadow-sm"
                  >
                    {isLogin ? t("loginPage.createAccount") : t("loginPage.signIn")}
                  </button>
                </div>

                {/* Owner / Employee toggle */}
                <div className="flex items-center gap-2 text-sm">
                  <span className={`font-medium ${!isEmployeeMode ? "text-emerald-700" : "text-slate-500"}`}>
                    {t("loginPage.businessOwner")}
                  </span>
                  <button
                    type="button"
                    onClick={toggleUserMode}
                    className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                      isEmployeeMode ? "bg-sky-500" : "bg-emerald-500"
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                        isEmployeeMode ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className={`font-medium ${isEmployeeMode ? "text-sky-700" : "text-slate-500"}`}>{t("loginPage.employee")}</span>
                </div>

                {!isLogin && (
                  <div className="text-xs text-slate-600">
                    {isEmployeeMode ? (
                      <span className="flex items-center gap-1">
                        <svg className="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        {t("loginPage.employeeInviteRequired")}
                      </span>
                    ) : (
                      <span>{t("loginPage.freeTrial")}</span>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
                  isAlreadyRegistered
                    ? "border-sky-200 bg-sky-50 text-slate-700"
                    : "border-red-200 bg-red-50 text-red-600"
                }`}>
                  <svg className="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    {isAlreadyRegistered ? (
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    ) : (
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    )}
                  </svg>
                  <div className="flex-1">
                    <span>{error}</span>
                    {isAlreadyRegistered && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsLogin(true);
                          setError("");
                          setIsAlreadyRegistered(false);
                        }}
                        className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-sky-600 hover:text-sky-700"
                      >
                        {t("loginPage.goToLogin")}
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10H3a1 1 0 110-2h10.586l-3.293-3.293a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  <svg className="mt-0.5 h-4 w-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{success}</span>
                </div>
              )}

              <form onSubmit={handleAuth} className="space-y-5">
                {!isLogin && (
                  <>
                    <label className="block text-sm font-semibold text-slate-800">
                      {t("loginPage.fullName")}
                      <input
                        type="text"
                        placeholder={t("loginPage.fullNamePlaceholder")}
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          resetMessages();
                        }}
                        className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        required
                      />
                    </label>
                    {!isEmployeeMode && (
                      <label className="block text-sm font-semibold text-slate-800">
                        {pendingAdminInvite ? t("loginPage.joiningBusiness") : t("loginPage.businessName")}
                        <input
                          type="text"
                          placeholder={pendingAdminInvite ? "" : t("loginPage.businessNamePlaceholder")}
                          value={businessName}
                          onChange={(e) => {
                            // Only allow editing if not an invited admin
                            if (!pendingAdminInvite) {
                              setBusinessName(e.target.value);
                              resetMessages();
                            }
                          }}
                          readOnly={!!pendingAdminInvite}
                          className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none ${
                            pendingAdminInvite
                              ? "border-emerald-300 bg-emerald-50 cursor-not-allowed"
                              : "border-slate-200 bg-white focus:border-emerald-300 focus:ring-2 focus:ring-emerald-200"
                          }`}
                          required={!pendingAdminInvite}
                        />
                        {pendingAdminInvite && (
                          <p className="mt-1.5 text-xs text-emerald-600">
                            {t("loginPage.invitedAsAdmin")}
                          </p>
                        )}
                      </label>
                    )}
                  </>
                )}

                <label className="block text-sm font-semibold text-slate-800">
                  {t("loginPage.emailAddress")}
                  <input
                    type="email"
                    placeholder={t("loginPage.emailPlaceholder")}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      resetMessages();
                      setInviteStatus("");
                      // Clear pending admin invite when email changes
                      if (!isEmployeeMode && !isLogin) {
                        setPendingAdminInvite(null);
                      }
                    }}
                    onBlur={(e) => {
                      if (isEmployeeMode) {
                        checkEmployeeInvite(e.target.value);
                      } else if (!isLogin) {
                        checkAdminInvite(e.target.value);
                      }
                    }}
                    className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    required
                  />
                  {/* Employee invite checking indicator */}
                  {isEmployeeMode && !isLogin && checkingEmployeeInvite && (
                    <div className="mt-1.5 text-xs flex items-center gap-1 text-slate-500">
                      {t("loginPage.checkingInvitation")}
                    </div>
                  )}
                  {/* Employee invite status */}
                  {isEmployeeMode && !isLogin && !checkingEmployeeInvite && inviteStatus && (
                    <div
                      className={`mt-1.5 text-xs flex items-center gap-1 ${
                        inviteStatus.startsWith("✓") ? "text-emerald-600" : "text-amber-600"
                      }`}
                    >
                      {inviteStatus}
                    </div>
                  )}
                  {/* Admin invite status */}
                  {!isEmployeeMode && !isLogin && pendingAdminInvite && (
                    <div className="mt-1.5 text-xs flex items-center gap-1 text-emerald-600">
                      {t("loginPage.adminInvitationFound")} <strong>{pendingAdminInvite.businessName}</strong>
                    </div>
                  )}
                  {/* Loading indicator for admin invite check */}
                  {!isEmployeeMode && !isLogin && checkingAdminInvite && (
                    <div className="mt-1.5 text-xs flex items-center gap-1 text-slate-500">
                      {t("loginPage.checkingInvitation")}
                    </div>
                  )}
                </label>

                <label className="block text-sm font-semibold text-slate-800">
                  {t("loginPage.password")}
                  <div className="mt-2 relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        const newPassword = e.target.value;
                        setPassword(newPassword);
                        resetMessages();
                        if (!isLogin) {
                          setPasswordStrength(getPasswordStrength(newPassword));
                        }
                      }}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 pr-12 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      aria-pressed={showPassword}
                    >
                      {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                    </button>
                  </div>
                  {/* Password strength indicator - only shown during signup */}
                  {!isLogin && password && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">{t("loginPage.passwordStrength")}</span>
                        <span className={`text-xs font-medium ${getStrengthLabel(passwordStrength).color}`}>
                          {getStrengthLabel(passwordStrength).label}
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${getStrengthLabel(passwordStrength).bgColor}`}
                          style={{ width: `${passwordStrength}%` }}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                        {passwordRequirements.map((req) => (
                          <div key={req.key} className="flex items-center gap-1.5 text-xs">
                            {req.test(password) ? (
                              <FiCheck className="h-3 w-3 text-emerald-500" />
                            ) : (
                              <FiX className="h-3 w-3 text-slate-300" />
                            )}
                            <span className={req.test(password) ? "text-emerald-600" : "text-slate-400"}>
                              {req.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </label>

                <button
                  type="submit"
                  disabled={loading || routing}
                  className="relative inline-flex w-full items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-emerald-500 via-sky-500 to-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-emerald-200/80 disabled:cursor-wait disabled:opacity-60"
                >
                  {loading ? t("loginPage.processing") : isLogin ? t("loginPage.signIn") : isEmployeeMode ? t("loginPage.joinTeam") : t("loginPage.createWorkspace")}
                </button>
              </form>

              <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
                <button
                  type="button"
                  onClick={toggleAuthMode}
                  className="font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  {isLogin ? t("loginPage.newToEffitrack") : t("loginPage.alreadyHaveAccount")}
                </button>
                {isLogin && (
                  <button
                    type="button"
                    onClick={handlePasswordReset}
                    className="font-semibold text-emerald-700 hover:text-emerald-800"
                  >
                    {t("loginPage.forgotPassword")}
                  </button>
                )}
              </div>

            </div>
          </section>

          {/* Brand / highlights card */}
          <aside className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white/80 backdrop-blur shadow-xl shadow-emerald-100/70 p-8">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full overflow-hidden flex items-center justify-center">
                  <img src={Logo} alt="EffiTrack" className="h-full w-full object-cover scale-110" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{t("loginPage.platform")}</h2>
                  <p className="text-xs text-slate-600">{t("loginPage.platformTagline")}</p>
                </div>
              </div>
              <div className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200">
                {t("loginPage.trialBadge")}
              </div>
            </div>

            <ul className="space-y-3 text-sm text-slate-700">
              {highlights.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">{t("loginPage.uptime")}</p>
                <p className="text-lg font-semibold text-slate-900">{t("loginPage.uptimeValue")}</p>
                <p className="text-xs text-slate-500 mt-1">{t("loginPage.last30Days")}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">{t("loginPage.onboarding")}</p>
                <p className="text-lg font-semibold text-slate-900">{t("loginPage.onboardingValue")}</p>
                <p className="text-xs text-slate-500 mt-1">{t("loginPage.averageSetup")}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs text-slate-700">
              <p className="font-semibold text-emerald-800">{t("loginPage.setupHelp")}</p>
              <p className="mt-2">
                {t("loginPage.setupHelpText")}
              </p>
            </div>

            <div className="space-y-2 text-xs text-emerald-700">
              <Link to="/walkthrough" className="inline-flex items-center gap-2 hover:text-emerald-800">
                {t("loginPage.needWalkthrough")}
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L13.586 10l-3.293-3.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M4 10a1 1 0 011-1h9a1 1 0 110 2H5a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
              <Link to="/import-guide" className="inline-flex items-center gap-2 hover:text-emerald-800">
                {t("loginPage.importGuide")}
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L13.586 10l-3.293-3.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M4 10a1 1 0 011-1h9a1 1 0 110 2H5a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
              <a href="mailto:support@effitrack.com" className="inline-flex items-center gap-2 hover:text-emerald-800">
                {t("loginPage.talkToSupport")}
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L13.586 10l-3.293-3.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M4 10a1 1 0 011-1h9a1 1 0 110 2H5a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Login;
